import { datadogRum } from '@datadog/browser-rum';
import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import FullPageLoader from './components/FullPageLoader';
import {
  loginWithRedirect,
  logout,
  ResolvedAuth,
  whenAuthReady,
} from './lib/auth';
import NotFound from './pages/notFound/NotFound';
import { routeTree } from './routeTree.gen';
import { queryClient } from './services/queryClient';
import { trackPendoEvent } from './utils/pendo';
import { PermissionString } from './utils/permissions';

export interface AuthContext {
  isAuthenticated: boolean;
  hasPermission: (permission: PermissionString) => boolean;
  login: (returnTo?: string) => void;
  logout: () => void;
  whenReady: () => Promise<ResolvedAuth>;
}

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthContext;
}

// Create the router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    // Routes under the authenticated gate receive the resolved snapshot via
    // its beforeLoad; these values are what routes outside it observe.
    auth: {
      isAuthenticated: false,
      hasPermission: () => false,
      login: (returnTo?: string) => {
        void loginWithRedirect(returnTo);
      },
      logout: () => {
        void logout();
      },
      whenReady: whenAuthReady,
    },
    queryClient,
  },
  defaultNotFoundComponent: NotFound,
  defaultPendingComponent: FullPageLoader,
});

// Track page views at router level. Each navigation produces one Datadog RUM
// view keyed by pathname or ddViewName.
router.subscribe('onResolved', ({ toLocation }) => {
  const routeMatches = router.state.matches;
  const ddViewName = routeMatches
    .map((match) => match.staticData?.ddViewName)
    .filter((name): name is string => Boolean(name))
    .pop(); // deepest match wins

  datadogRum.startView({
    name: ddViewName ?? toLocation.pathname,
    url: toLocation.pathname,
  });

  trackPendoEvent('page_view', {
    page_name: toLocation.pathname,
  });
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    ddViewName?: string;
  }
}
